import { motion, useReducedMotion } from "motion/react";
import type { ReactNode } from "react";

import {
  REVEAL_EASE_OUT,
  REVEAL_GROUP_DELAY_CHILDREN,
  REVEAL_GROUP_ITEM_DURATION,
  REVEAL_GROUP_STAGGER_CHILDREN,
  REVEAL_Y,
} from "@/app/common/functions/motion/config";
import { resolveRevealGroupAnimate } from "@/app/common/functions/motion/revealAnimation";
import { useReveal } from "@/app/common/hooks/useReveal";

const container = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: REVEAL_GROUP_STAGGER_CHILDREN,
      delayChildren: REVEAL_GROUP_DELAY_CHILDREN,
    },
  },
};

const item = {
  hidden: { opacity: 0, y: REVEAL_Y },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: REVEAL_GROUP_ITEM_DURATION, ease: REVEAL_EASE_OUT },
  },
};

export type RevealGroupProps = {
  /** A layout (a Grid, a Stack) whose items are `RevealItem`s. */
  children: ReactNode;
};

/**
 * Staggers its `RevealItem`s into view. Motion's variants reach the items
 * through React, not the DOM, so the group wraps the list's layout and the
 * items sit inside it as the list's own children.
 */
export function RevealGroup({ children }: RevealGroupProps) {
  const shouldReduceMotion = useReducedMotion();
  const [ref, hasRevealed] = useReveal();

  return (
    <motion.div
      ref={ref}
      initial={shouldReduceMotion ? false : "hidden"}
      animate={resolveRevealGroupAnimate(shouldReduceMotion, hasRevealed)}
      variants={container}
      transition={shouldReduceMotion ? { duration: 0 } : undefined}
    >
      {children}
    </motion.div>
  );
}

export type RevealItemProps = {
  children: ReactNode;
  /** `li` inside a list's layout. */
  as?: "div" | "li";
};

/** One item of a `RevealGroup`. */
export function RevealItem({ children, as = "div" }: RevealItemProps) {
  // Always keep `item` as the variants, even under reduced motion: dropping
  // to `undefined` disconnects this element from the parent RevealGroup's
  // now-corrected animate="show" cascade, reintroducing the same
  // stuck-invisible bug at the item level instead of the group level.
  return as === "li" ? (
    <motion.li variants={item}>{children}</motion.li>
  ) : (
    <motion.div variants={item}>{children}</motion.div>
  );
}
